
import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';


const makeDateTimeOrNull = (iso_string) => {
    return iso_string ? DateTime.fromISO(iso_string) : null;
}

const workflowStatusObjects = {
    // a mapping of status_value (which is currently just a front-end thing) to other things needed for status-object-badge
    initial: {
        value: 'initial',
        title: 'Draft',
        color_variant: 'primary',
        icon: 'record-fill',
    },
    active: {
        value: 'active',
        title: 'Active',
        color_variant: 'success',
        icon: 'record-fill',
    },
    final: {
        value: 'final',
        title: 'Closed',
        color_variant: 'danger',
        icon: 'record-fill',
    },
};

const getWorkflowStatusObject = (statusValue) => {
    const obj = workflowStatusObjects[statusValue];
    // now make a copy to be safe, or default to empty object if we don't have valid value
    return obj ? { ...obj } : {};
}

const getWorkflowStatusOptions = () => {
    // export this, then call this in Vue component to populate the dropdown for what status options are above
    // using it make sure the values you're assigning below match the options in select dropdown
    // return array of objects with { value, text }
    // should add 'all' when making options as well
    const statusMapping = cloneDeep(workflowStatusObjects);
    return Object.entries(statusMapping).map(([status_value, status_object]) => {
        return {value: status_value, text: status_object.title };
    }) 
};


class WorkflowRun {
    constructor({  // you can set defaults for constructor params here, or just let them be undefined and set in constructor method
        id,
        created,
        modified,
        title,
        workflow_run_number,
        metadata,
        metadata_schema, 
        sm_state,
        last_sm_state,
        state_dict,
        sm_state_changed_on,
        is_workflow_started,
        possible_transitions,
        workflow_spec,
        workflow_spec_id,
        workflow_spec_name,  // we may pass in separately
        owner,
        owner_id,
        vendor,
        vendor_id,
        vendor_members,
        internal_members,
        is_internal_only,
        urls,  // object with all urls nested inside
        // THINGS ONLY FOR LIST ENDPOINTS
        current_step_title, // can use this when you don't want to init StateMachine to make state_dict
        active_tasks,  // list/array of tasks that are currently active, the things this Run is waiting on -- much better than just showing the step
        past_due_tasks, // list/array of tasks that are past deadline, { id, title, sm_state, deadline, assigned_tus[tu_obj] }
        current_step_is_initial, // boolean
        current_step_is_final, // boolean
        current_step_is_completed, // boolean
        current_step_is_active, // boolean
        is_waiting_on_tu, // boolean, null if no TU -- is this run currently waiting on the current TU
        closed_note,  // string with any note set when it was closed, could be empty string
    }) {
        this.id = id;
        this.pk = id;
        this.created = makeDateTimeOrNull(created);
        this.modified = makeDateTimeOrNull(modified);
        this.title = title ?? '';
        this.workflow_run_number = workflow_run_number ?? '';
        this.metadata = metadata ?? {};
        this.metadata_schema = metadata_schema;  // we get it from workflow_spec via model method
        this.sm_state = sm_state ?? '';
        this.last_sm_state = last_sm_state ?? '';
        this.state_dict = state_dict ?? {};
        this.sm_state_changed_on = makeDateTimeOrNull(sm_state_changed_on);
        this.is_workflow_started = is_workflow_started;
        this.possible_transitions = possible_transitions ?? [];
        this.workflow_spec = workflow_spec ?? {};
        this.workflow_spec_id = workflow_spec_id ?? this.workflow_spec.id ?? '';
        this.workflow_spec_name = workflow_spec_name ?? this.workflow_spec.name ?? '';
        this.owner = owner ?? (owner_id ? {id: owner_id} : {});
        this.owner_id = owner_id ?? this.owner.id ?? '';
        this.vendor = vendor ?? (vendor_id ? {id: vendor_id} : {});  // so we always have vendor.id if we have a vendor
        this.vendor_id = vendor_id ?? this.vendor.id ?? '';
        this.vendor_members = vendor_members;
        // this.vendor_members_ids also
        this.internal_members = internal_members;
        this.is_internal_only = is_internal_only;
        this.urls = urls ?? {};
        // FOR LIST ENDPOINTS
        this.current_step_title = current_step_title ?? '';
        this.active_tasks = active_tasks ?? [];
        this.has_active_tasks = (this.active_tasks.length > 0);
        this.past_due_tasks = past_due_tasks ?? [];
        this.has_past_due_tasks = (this.past_due_tasks.length > 0);
        this.current_step_is_initial = current_step_is_initial;  // boolean, will be undefined or null if we don't have it
        this.current_step_is_final = current_step_is_final;  // boolean, will be undefined or null if we don't have it
        this.current_step_is_completed = current_step_is_completed;  // boolean, will be undefined or null if we don't have it
        this.current_step_is_active = current_step_is_active;  // boolean, will be undefined or null if we don't have it
        // now we'll set 'status' -- either 'initial', 'active', 'closed' (or similar)
        // should match whatever we have in dropdown filter in list table -- WE DEFINE THEM ABOVE
        this.status = this.current_step_is_active ? 'active' : (this.current_step_is_initial ? 'initial' : 'final');
        this.status_object = getWorkflowStatusObject(this.status);
        this.is_waiting_on_tu = is_waiting_on_tu;
        this.closed_note = closed_note ?? '';
    }
}

class DwfTask {
    constructor(apiTaskObj) {
        Object.entries(apiTaskObj).forEach(([key, value]) => {
            this[key] = value;  // just assigne everything as class property 
        });
        const assigneeAutoFields = ['assigned_tus'];  // hides assigned_tus select box if any of these are in 'auto_fields'
        const deadlineAutoFields = ['deadline', 'deadline_days', ];  // hides deadline_days input if any of these are in 'auto_fields'
        // TODO - you now also auto-set the deadline using 'deadline_kwargs' in 'context_data' - which would not show up here, since its not its own field... figure that our later if needed
        const hasAutoFields = !!this.auto_fields && Array.isArray(this.auto_fields);
        this.showAssigneeInput = hasAutoFields ? !this.auto_fields.some(auto => assigneeAutoFields.includes(auto)) : true;
        // bad name this.showDeadlineDaysInput = hasAutoFields ? !this.auto_fields.some(auto => deadlineAutoFields.includes(auto)) : true;
        this.canSetDeadlineDays = hasAutoFields ? !this.auto_fields.some(fld => deadlineAutoFields.includes(fld)) : true;
        this.showSaveButton = this.showAssigneeInput || this.canSetDeadlineDays;
    }
}


export {
    WorkflowRun,
    DwfTask,
    getWorkflowStatusOptions,
}